import { animate, style, transition, trigger } from '@angular/animations';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { DataSet, Record as DataCoreRecord } from '@mtna/data-core-ui';
import { UpdateQueryParameters } from '@mtna/data-query-ui';
import { DURATION_MEDIUM_COLLAPSE, EASING_STANDARD } from '@mtna/lib-ui';
import { trackByCoreResource, Variable } from '@mtna/pojo-consumer-ui';

import { RdsResizeableTableComponent } from '../core/components/resizeable-table.component';
import { RdsCodeDisplayType } from '../core/models/code-display-type.model';
import { RdsRecordUtil } from '../core/utils/record-util';

export const RDS_SELECT_TABLE_MIN_COL_WIDTH = 50;

export const rdsEditableColumnFieldTypeIDs = ['CHECK'] as const;
export type RdsEditableColumnFieldType = typeof rdsEditableColumnFieldTypeIDs[number];

export class RdsSelectTableEditEvent {
  constructor(public recordLayoutUri: string, public update: UpdateQueryParameters) {}
}

/**
 * Custom mat-table used to display the results of a RDS select query
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-select-table',
  templateUrl: './select-table.component.html',
  styleUrls: ['./select-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('rowsAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate(`${DURATION_MEDIUM_COLLAPSE} ${EASING_STANDARD}`, style({ opacity: 1 }))]),
    ]),
  ],
})
export class RdsSelectTableComponent extends RdsResizeableTableComponent {
  /** Display configuration for codes in the table. Defaults to âNAMEâ */
  @Input() codeDisplayType: RdsCodeDisplayType = 'NAME';
  /** The columns that have been used as a filter */
  @Input() set columnsWithFilters(variables: Array<Variable>) {
    this._appliedFilters = {};
    variables.forEach((v) => (this._appliedFilters[v.uri] = true));
  }
  /** The result of the RDS select query to display in the table */
  @Input()
  get dataSet(): DataSet {
    return this._dataSet;
  }
  set dataSet(ds: DataSet) {
    this._dataSet = ds;
    // Reset arrays and maps when DataSet changes
    this._editableColumns = this.getDefaultEditableColumns();
    this._lockedColumns = {};
    this._keyColumns = {};
    this._lastLockedVariable = null;

    // determine and set displayable columns for the table
    if (!this.dataSet) {
      this.displayedColumns = [];
      this._readOnlyVariables = [];
    } else {
      // Setup the locked variable map
      if (!!this.dataSet.dataSetInformation && !!this.dataSet.dataSetInformation.lockedVariableUris) {
        this.dataSet.dataSetInformation.lockedVariableUris.forEach((uri) => (this._lockedColumns[uri] = true));
        // Set the last locked variable
        this._lastLockedVariable = this.dataSet.dataSetInformation.lockedVariableUris[
          this.dataSet.dataSetInformation.lockedVariableUris.length - 1
        ];
      }

      // Setup the key variable map
      if (!!this.dataSet.recordLayout && !!this.dataSet.recordLayout.primaryKey && !!this.dataSet.recordLayout.primaryKey.variables) {
        this.dataSet.recordLayout.primaryKey.variables.forEach((variable) => (this._keyColumns[variable.uri] = true));
      }

      // Initialize displayed columns after key map is set,
      // this is required to determine what columns are displayed
      this.initializeDisplayedColumns();

      const editableFields: { [uri: string]: Variable } = {};

      // If the dataset has an editConfiguration,
      // Populate an object map of editable variables
      if (
        !!this.dataSet.editConfiguration &&
        !!this.dataSet.editConfiguration.editableFields &&
        !!this.dataSet.editConfiguration.editableFields.length
      ) {
        this.dataSet.editConfiguration.editableFields.forEach((field) => {
          // Find the variable in the record layout
          const editableVariable = this.dataSet.recordLayout.variables.find((v) => v.uri === field.variableUri);
          // Set the editable fields map
          editableFields[field.variableUri] = editableVariable;

          /* Determine the specific control for the edit field */
          if (editableVariable.dataType === 'BOOLEAN') {
            // A boolean is represented by a CHECKBOX
            const existingCheckboxColumns = this._editableColumns.CHECK || [];
            this._editableColumns.CHECK = [...existingCheckboxColumns, editableVariable];
          }
        });
      }

      // Populate the array of readonly variables by filtering out
      // the variables that exist in the editConfiguration
      this._readOnlyVariables = this.dataSet.recordLayout.variables.filter((v) => !editableFields[v.uri]);
    }

    if (this.enableColumnResize) {
      this.initColumnWidths(ds);
    }
  }
  private _dataSet: DataSet;

  /**
   * Whether ghost elements should be rendered to show a loading state.
   * A fake/empty data set should be provided so no values show in the table.
   */
  @Input() ghost: boolean;
  /** Indicates which column is inspected */
  @Input() inspectedVariable: Variable;

  /** Whether to show the columns that are used as primary keys, defaults to true */
  @Input()
  get showPrimaryKeyColumns(): boolean {
    return this._showPrimaryKeyColumns;
  }
  set showPrimaryKeyColumns(value: boolean) {
    const newValue = coerceBooleanProperty(value);
    if (this.showPrimaryKeyColumns !== newValue) {
      this._showPrimaryKeyColumns = newValue;
      // Re-initialize the displayed columns if the DataSet has already been set
      if (!!this.dataSet) {
        this.initializeDisplayedColumns();
      }
      this.cdr.markForCheck();
    }
  }
  private _showPrimaryKeyColumns = true;

  /**
   * Whether the toggled columns represent the selected, or unselected.
   * For instance, if the user selects 100+ columns, and unselects 5,
   * the 'toggled' columns will be the 5 unselected & 'toggledAreSelected' will be false;
   * i.e.
   * user selects 95 out of 100 => toggledAreSelected: false, toggled.length: 5;;
   * user selects 5 out of 100 => toggledAreSelected: true, toggled.length: 5;
   */
  @Input() toggledAreSelected = true;
  /** The columns that are toggled for including/excluding in packaging */
  @Input() set toggledColumns(variables: Array<Variable>) {
    this._toggledColumns = {};
    variables.forEach((v) => (this._toggledColumns[v.uri] = true));
  }

  /** Emitted when record has been edited */
  @Output() edit = new EventEmitter<RdsSelectTableEditEvent>();
  /** Emitted when a column is inspectected/clicked */
  @Output() inspectVariable = new EventEmitter<Variable>();

  get areColumnsSet(): boolean {
    return !!this.dataSet;
  }

  get minColWidth(): number {
    return RDS_SELECT_TABLE_MIN_COL_WIDTH;
  }

  /** Map of variable uris applied as filters, used for decorators */
  _appliedFilters: { [uri: string]: boolean } = {};
  /** Map of primary key variable uris, used for decorators */
  _keyColumns: { [uri: string]: boolean } = {};
  /** Last locked variable */
  _lastLockedVariable: string;
  /** Map of locked variable uris, used for decorators */
  _lockedColumns: { [uri: string]: boolean } = {};
  /** The non-editable variables */
  _readOnlyVariables: Array<Variable>;
  /** Map of toggled variable uris to indicate which have been included/excluded for packaging, used for decorators */
  _toggledColumns: { [uri: string]: boolean } = {};
  /** TrackBy function for the column for loop */
  _trackByCr = trackByCoreResource;
  /** Variables split up into table columns by type */
  _editableColumns: Record<RdsEditableColumnFieldType, Variable[]> = this.getDefaultEditableColumns();

  constructor(cdr: ChangeDetectorRef) {
    super(cdr);
  }

  handleCheckChange(event: MatCheckboxChange, record: DataCoreRecord, variable: Variable) {
    this.edit.emit(
      new RdsSelectTableEditEvent(this.dataSet.recordLayout.uri, {
        variableUri: variable.uri,
        value: event.checked,
        key: RdsRecordUtil.getKey(record, this.dataSet.recordLayout.primaryKey),
      })
    );
  }

  handleInspectVariable(variable: Variable) {
    // prevent selection while resizing
    if (!this._resizingColumn) {
      this.inspectVariable.emit(variable);
    }
  }

  protected getColumnWidth(id: string): number {
    const variable = this.dataSet.recordLayout.variables.find((v) => v.uri === id);
    if (!!variable && ((variable.dataType === 'NUMERIC' && !variable.classificationUri) || variable.dataType === 'GHOST')) {
      return 100;
    }
    return super.getColumnWidth(id);
  }

  protected getDefaultEditableColumns(): Record<RdsEditableColumnFieldType, Variable[]> {
    return { CHECK: [] };
  }

  /**
   * Initialize the displayed columns for the table.
   * Take into account whether priamry keys should be hidden.
   */
  protected initializeDisplayedColumns() {
    const allVariableUris = this.dataSet.recordLayout.variables.map((v) => v.uri);

    this.displayedColumns = this.showPrimaryKeyColumns ? allVariableUris : allVariableUris.filter((uri) => !this._keyColumns[uri]);
  }

  protected initColumnWidths(dataset: DataSet) {
    if (!!dataset) {
      this.dataSet.recordLayout.variables.forEach((v) => {
        if (!this._columnWidth[v.uri]) {
          this._columnWidth[v.uri] = !!v.name ? v.name.length * 8 + 24 : this.getColumnWidth(v.uri); // 1 char = 8px + 24px (padding)
        }
      });
    } else {
      super.initializeColumnWidths();
    }
  }
}
